import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { Mic, Square } from 'lucide-react'

import { Button, Modal } from '@/components/ui'
import { ApiError } from '@/internationalization/api-client'
import { formatTimecode } from '@/internationalization/format'
import { qk } from '@/internationalization/query-keys'
import { toast } from '@/stores'

import { meetingsApi } from '../api'
import {
  DeviceRecorder,
  deviceRecordingSupported,
  type DeviceRecorderProgress,
} from '../device-recorder'

type Phase = 'idle' | 'starting' | 'recording' | 'saving' | 'error'

const NO_PROGRESS: DeviceRecorderProgress = { captured: 0, uploaded: 0, failed: 0 }

function describeError(error: unknown): string {
  if (error instanceof DOMException) {
    if (error.name === 'NotAllowedError' || error.name === 'SecurityError') {
      return "Microphone access is blocked. Allow it in your browser's site settings and try again."
    }
    if (error.name === 'NotFoundError') return 'No microphone was found on this device.'
    if (error.name === 'NotReadableError') {
      return 'The microphone is in use by another application.'
    }
  }
  // A 4xx is a considered answer worth showing as-is; a 5xx is for the log.
  if (error instanceof ApiError) {
    return error.status < 500 ? error.message : 'Something went wrong. Try again shortly.'
  }
  if (error instanceof Error && error.message) return error.message
  return 'Something went wrong. Try again shortly.'
}

/**
 * "Record now": record this device's microphone, no bot, no meeting link.
 *
 * MODAL FOR THE WHOLE RECORDING, deliberately. The recorder lives in this
 * component, so the dialog cannot be dismissed while it runs -- closing would
 * otherwise orphan a live microphone with nothing on screen saying so. Leaving
 * the page is warned against, and if the component unmounts anyway (the back
 * button) the recording is stopped and SAVED rather than dropped: a recording
 * cannot be repeated.
 */
export function DeviceRecorderDialog({
  isOpen,
  onClose,
}: {
  isOpen: boolean
  onClose: () => void
}) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [title, setTitle] = useState('')
  const [phase, setPhase] = useState<Phase>('idle')
  const [error, setError] = useState<string | null>(null)
  const [elapsed, setElapsed] = useState(0)
  const [level, setLevel] = useState(0)
  const [progress, setProgress] = useState<DeviceRecorderProgress>(NO_PROGRESS)
  const [inputLost, setInputLost] = useState(false)
  const recorderRef = useRef<DeviceRecorder | null>(null)
  const startedAtRef = useRef(0)
  const maxSecondsRef = useRef(Number.POSITIVE_INFINITY)

  const supported = deviceRecordingSupported()
  const busy = phase === 'starting' || phase === 'recording' || phase === 'saving'
  // Audio exists that the server does not have yet.
  const unsaved = recorderRef.current !== null && phase !== 'idle'

  const reset = () => {
    setTitle('')
    setPhase('idle')
    setError(null)
    setElapsed(0)
    setLevel(0)
    setProgress(NO_PROGRESS)
    setInputLost(false)
  }

  const save = useCallback(async () => {
    const recorder = recorderRef.current
    if (!recorder) return
    setPhase('saving')
    setError(null)
    try {
      await recorder.stop()
      const meetingId = await recorder.finish()
      recorderRef.current = null
      void queryClient.invalidateQueries({ queryKey: qk.meetings.all })
      toast.success(
        'Recording saved',
        'The transcript and summary will be ready in a few minutes.',
      )
      reset()
      onClose()
      navigate(`/meetings/${meetingId}`)
    } catch (caught) {
      setPhase('error')
      setError(describeError(caught))
    }
  }, [navigate, onClose, queryClient])

  const start = async () => {
    setError(null)
    setPhase('starting')
    const recorder = new DeviceRecorder()
    try {
      // The microphone first: a refusal must not leave a recording behind.
      await recorder.open()
    } catch (caught) {
      setPhase('idle')
      setError(describeError(caught))
      return
    }
    try {
      const { meetingId, maxSeconds } = await meetingsApi.deviceRecording.start(
        title.trim() || undefined,
      )
      maxSecondsRef.current = maxSeconds
      recorder.onLevel = setLevel
      recorder.onProgress = setProgress
      // NOT a reason to save: a headset dropping out for a second must not
      // end a conversation that is still going on. The recorder tries the
      // default microphone again by itself; this only tells the person.
      recorder.onInputLost = () => setInputLost(true)
      recorder.onInputRestored = () => setInputLost(false)
      recorder.onRefused = (refusal) => {
        setPhase('error')
        setError(describeError(refusal))
      }
      recorderRef.current = recorder
      await recorder.begin(meetingId)
    } catch (caught) {
      recorder.abandon()
      recorderRef.current = null
      setPhase('idle')
      setError(describeError(caught))
      return
    }
    startedAtRef.current = Date.now()
    setElapsed(0)
    setPhase('recording')
    void queryClient.invalidateQueries({ queryKey: qk.meetings.all })
  }

  const close = () => {
    if (busy) return
    // Only reachable from `error` with audio held: the person has seen the
    // failure and chosen to give up on it.
    recorderRef.current?.abandon()
    recorderRef.current = null
    reset()
    onClose()
  }

  useEffect(() => {
    if (phase !== 'recording') return
    const id = window.setInterval(() => {
      const seconds = (Date.now() - startedAtRef.current) / 1000
      setElapsed(seconds)
      // The server transcribes only this much; stopping here saves what it
      // will use instead of recording on into nothing.
      if (seconds >= maxSecondsRef.current) {
        toast.info(
          'Recording stopped at the time limit',
          `Recordings are limited to ${formatTimecode(maxSecondsRef.current)}.`,
        )
        void save()
      }
    }, 500)
    return () => window.clearInterval(id)
  }, [phase, save])

  useEffect(() => {
    if (!busy && !unsaved) return
    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault()
      event.returnValue = ''
    }
    window.addEventListener('beforeunload', warn)
    return () => window.removeEventListener('beforeunload', warn)
  }, [busy, unsaved])

  useEffect(
    () => () => {
      const recorder = recorderRef.current
      if (recorder) void recorder.stop().then(() => recorder.finish()).catch(() => undefined)
    },
    [],
  )

  const footer =
    phase === 'recording' ? (
      <Button variant="danger" leftIcon={<Square className="h-4 w-4" />} onClick={() => void save()}>
        Stop and save
      </Button>
    ) : phase === 'saving' ? (
      <Button variant="primary" isLoading disabled>
        Saving…
      </Button>
    ) : phase === 'error' && recorderRef.current?.refused ? (
      // A refusal retrying cannot change: offering "Try again" is a loop.
      <Button variant="secondary" onClick={close}>
        Close
      </Button>
    ) : phase === 'error' && recorderRef.current ? (
      <>
        <Button variant="secondary" onClick={close}>
          Discard
        </Button>
        <Button variant="primary" onClick={() => void save()}>
          Try again
        </Button>
      </>
    ) : (
      <>
        <Button variant="secondary" onClick={close} disabled={phase === 'starting'}>
          Cancel
        </Button>
        <Button
          variant="primary"
          leftIcon={<Mic className="h-4 w-4" />}
          isLoading={phase === 'starting'}
          disabled={!supported || phase === 'starting'}
          onClick={() => void start()}
        >
          Start recording
        </Button>
      </>
    )

  return (
    <Modal
      isOpen={isOpen}
      onClose={close}
      closeOnOverlayClick={false}
      title="Record now"
      description="Record a conversation with this device's microphone. No bot joins anything."
      footer={footer}
    >
      <div className="space-y-4">
        {phase === 'idle' || phase === 'starting' ? (
          <>
            <div>
              <label htmlFor="device-recording-title" className="mb-1.5 block text-sm font-medium text-fg">
                Title <span className="font-normal text-fg-subtle">(optional)</span>
              </label>
              <input
                id="device-recording-title"
                type="text"
                maxLength={200}
                value={title}
                onChange={(event) => setTitle(event.target.value)}
                placeholder="e.g. Client visit — Northwind"
                className="w-full rounded-md border border-border bg-surface px-3 py-2 text-base text-fg outline-none placeholder:text-fg-subtle focus:border-brand"
              />
            </div>
            <p className="text-sm text-fg-muted">
              Speakers are told apart by voice and labelled Speaker 0, Speaker 1, … rather than by
              name. Keep this tab open until you stop.
            </p>
            {!supported && (
              <p className="text-sm text-danger">This browser cannot record audio. Use a recent Chrome, Edge, Firefox or Safari.</p>
            )}
          </>
        ) : (
          <div className="space-y-3 text-center">
            <div className="font-mono text-4xl tabular-nums text-fg" aria-live="off">
              {formatTimecode(elapsed)}
            </div>
            {phase === 'recording' && (
              <div className="mx-auto h-2 w-48 overflow-hidden rounded-full bg-surface-muted" aria-hidden>
                <div
                  className="h-full rounded-full bg-brand transition-[width] duration-150"
                  style={{ width: `${Math.min(1, level * 4) * 100}%` }}
                />
              </div>
            )}
            {phase === 'recording' && inputLost && (
              <div className="space-y-2" role="alert">
                <p className="text-sm text-danger">
                  The microphone was disconnected. Nothing recorded so far is lost.
                </p>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => void recorderRef.current?.retryInput()}
                >
                  Reconnect microphone
                </Button>
              </div>
            )}
            <p className="text-sm text-fg-muted">
              {phase === 'recording' && 'Recording. Keep this tab open until you stop.'}
              {phase === 'saving' && 'Uploading the rest of the recording…'}
              {progress.captured > 0 &&
                ` ${progress.uploaded} of ${progress.captured} part(s) uploaded.`}
            </p>
          </div>
        )}
        {error && (
          <p className="text-sm text-danger" role="alert">
            {error}
          </p>
        )}
      </div>
    </Modal>
  )
}
